import moment from 'moment'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import { Avatar } from '@/components/avatar'
import { MobileDrawer } from '@/components/drawer'
import EndTenancyButton from '@/components/end-tenancy-button'
import useMediaQuery from '@/hooks/use-media-query'
import { select } from '@/store'
import { colors, fontSizes, fontWeights } from '@/styles'
import { isNullOrEmpty } from '@/utils'
import { getImageUrl } from '@/utils/image'
import { CalendarAlt } from '@/components/icons'
import tenancyIcon from './tenancy-icon.svg'

const Wrapper = styled.div`
  overflow: hidden;
  width: 100%;
  height: min-content;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border: 1px solid ${colors.grey};
  border-radius: 6px;
  background: ${colors.white};
  grid-column: 2;

  @media screen and (max-width: 1248px) {
    width: 100%;
    grid-column: auto;
  }
`

const TopColor = styled.div`
  border-radius: 6px 6px 0 0;
  background: ${colors.secondary};
  height: 8px;
`

const InnerWrapper = styled.div`
  padding: 10px 25px 35px;
`

const Tenants = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;
  margin-bottom: 30px;
  border-bottom: 1px solid ${colors.grey};
`

const Tenant = styled.div`
  display: flex;
  align-items: center;

  :not(:last-child) {
    margin-bottom: 20px;
  }
`

const Name = styled.p`
  overflow-wrap: break-word;
  word-break: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.39px;
  line-height: 22px;
  margin: 0;
`

const TenancyInfo = styled.div`
  display: flex;
  flex-direction: column;

  > :not(:last-child) {
    margin-bottom: 20px;
  }
`

const TenancyDates = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const DateText = styled.p`
  color: ${colors.regular};
  font-size: 15px;
  letter-spacing: -0.39px;
  line-height: 22px;
  margin: 0;
`

const endTenancyButtonStyle = css`
  color: ${colors.secondary};
  font-size: ${fontSizes.regular};
  line-height: 18px;
  text-decoration: underline;
  width: fit-content;
`

const Title = styled.span`
  font-size: 20px;
  font-weight: 700;
  margin: 20px 24px;
`

const TenantInfo = ({ tenants, contract }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [height, setHeight] = useState(0)
  const contentRef = useRef(null)
  const listingId = contract.listing_id

  useEffect(() => {
    setHeight(contentRef.current.clientHeight)
  }, [tenants])

  useEffect(() => {
    dispatch.endTenancy.loadAll({ listingId })
  }, [dispatch.endTenancy, listingId])

  const endTenancyRequestData = useSelector(state =>
    select.endTenancy.endTenancyRequestData(state, {
      listingId
    })
  )

  const noTenants = isNullOrEmpty(contract.tenants)

  const getDates = () => {
    const prefix = contract.end_date ? '' : `${t('l.tenants.view.since.text', 'Since')} `
    const startDate = moment(contract.start_date).format('MMM DD, YYYY')
    const endDate = contract.end_date
      ? ` - ${moment(contract.end_date).format('MMM DD, YYYY')}`
      : ''
    return `${prefix}${startDate}${endDate}`
  }

  return (
    <Wrapper>
      <TopColor />

      <Title>{t('l.tenants.view.tenant_info.title', 'Rental Info')}</Title>

      <InnerWrapper ref={contentRef}>
        {!noTenants && (
          <Tenants>
            {tenants.map(tenant => (
              <Tenant key={`tenant-${tenant.id}`}>
                <Avatar
                  location={getImageUrl(tenant.avatar?.aws_s3_key, {
                    width: 80,
                    height: 80
                  })}
                  width={40}
                  height={40}
                  right={10}
                  shadow
                />
                <Name>{`${tenant.first_name} ${tenant.last_name}`}</Name>
              </Tenant>
            ))}
          </Tenants>
        )}
        <TenancyInfo>
          <TenancyDates>
            <CalendarAlt width={25} />
            <DateText>{getDates()}</DateText>
          </TenancyDates>
          <EndTenancyButton
            listingId={contract.listing_id}
            tenants={contract.tenants}
            stateMachine={contract.listing_state_machine}
            endTenancyRequestData={endTenancyRequestData}
            customStyle={endTenancyButtonStyle}
          />
        </TenancyInfo>
      </InnerWrapper>
    </Wrapper>
  )
}

export default TenantInfo
